// ── Tema: paletas, estilos visuais e contexto ───────────────────────────────
// Clássico (original), Tático, Foco e Institucional (padrão grafite+bronze).
import { useState, useEffect, createContext, useContext } from "react";
import { loadLS } from "./storage";

export const ACCENTS = [
  { id: "green",  label: "Verde",    hex: "#34d399" },
  { id: "blue",   label: "Azul",     hex: "#60a5fa" },
  { id: "red",    label: "Vermelho", hex: "#dc2626" },
  { id: "yellow", label: "Amarelo",  hex: "#fbbf24" },
  { id: "purple", label: "Roxo",     hex: "#a78bfa" },
  { id: "pink",   label: "Rosa",     hex: "#f472b6" },
  { id: "orange", label: "Laranja",  hex: "#fb923c" },
  { id: "cyan",   label: "Ciano",    hex: "#22d3ee" },
];

// Derive a dark tinted bg from the accent color
export function hexToRgb(hex) {
  const h = hex.replace("#","");
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}
export function tintDark(hex, strength=0.08) {
  const [r,g,b] = hexToRgb(hex);
  const toH = v => Math.round(v*strength).toString(16).padStart(2,"0");
  return "#" + toH(r) + toH(g) + toH(b);
}
export function tintLight(hex, strength=0.06) {
  const [r,g,b] = hexToRgb(hex);
  const blend = v => Math.round(255 - (255-v)*strength);
  return "#" + [r,g,b].map(v=>blend(v).toString(16).padStart(2,"0")).join("");
}

// Visual original do G17 (estilo "Clássico"): mantido exatamente como era.
export function makeClassicTheme(mode, accentId) {
  const a  = ACCENTS.find(x => x.id === accentId) || ACCENTS[0];
  const ac = a.hex;
  if (mode === "dark") {
    const [r,g,b] = hexToRgb(ac);
    const dk = (v, f) => Math.round(v*f).toString(16).padStart(2,"0");
    const bg      = "#" + dk(r,0.055) + dk(g,0.055) + dk(b,0.055);
    const bgSide  = "#" + dk(r,0.07)  + dk(g,0.07)  + dk(b,0.07);
    const bgCard  = "#" + dk(r,0.09)  + dk(g,0.09)  + dk(b,0.09);
    const bgInner = "#" + dk(r,0.11)  + dk(g,0.11)  + dk(b,0.11);
    const border  = "#" + dk(r,0.18)  + dk(g,0.18)  + dk(b,0.18);
    const borderIn= "#" + dk(r,0.13)  + dk(g,0.13)  + dk(b,0.13);
    return {
      mode, ac,
      acDim:     ac + "20",
      acBorder:  ac + "40",
      bg, bgSide, bgCard, bgInner, border, borderIn,
      text:      "#e8eef0",
      textMid:   "#b0c4cc",
      textDim:   "#607080",
      textFaint: "#3a5060",
      textGhost: "#1e3040",
    };
  }
  // Light mode: white cards with subtle accent tint
  return {
    mode, ac,
    acDim:     ac + "18",
    acBorder:  ac + "50",
    bg:        "#f4f6f8",
    bgSide:    "#e8ecf0",
    bgCard:    "#ffffff",
    bgInner:   "#f0f4f6",
    border:    "#d0d8e0",
    borderIn:  "#e0e8ee",
    text:      "#0d1520",
    textMid:   "#1a2a38",
    textDim:   "#4a6070",
    textFaint: "#6a8090",
    textGhost: "#9ab0bc",
  };
}

// ── ESTILOS VISUAIS ─────────────────────────────────────────────────────────
// O usuário escolhe no Perfil. "classico" = visual original; os outros três
// foram desenhados na nova identidade (grafite neutro, calmo e institucional).
export const FONT_STACKS = {
  outfit:  "'Outfit','DM Sans',system-ui,sans-serif",
  inter:   "'Inter',system-ui,-apple-system,'Segoe UI',sans-serif",
  barlow:  "'Barlow Condensed','Inter',sans-serif",
  saira:   "'Saira Condensed','Source Sans 3',sans-serif",
  source:  "'Source Sans 3',system-ui,sans-serif",
  mono:    "'JetBrains Mono',ui-monospace,monospace",
};

export const THEME_STYLES = [
  { id: "institucional", label: "Institucional",   desc: "Fundo sóbrio com detalhes em metal (padrão: grafite e bronze)" },
  { id: "tatico",        label: "Tático refinado", desc: "Grafite neutro com uma cor de destaque" },
  { id: "foco",          label: "Foco calmo",      desc: "Limpo e silencioso, claro ou escuro" },
  { id: "classico",      label: "Clássico",        desc: "O visual original do G17", adminOnly: true },
];
// Padrão para todo mundo: Institucional, grafite e bronze. Só muda se a pessoa escolher outro em Perfil.
export const DEFAULT_THEME_STYLE = "institucional:grafite:bronze";
export const DEFAULT_INST_BASE = "grafite", DEFAULT_INST_METAL = "bronze";
// Preferência de aparência: só vale a gravada no navegador se foi escolha manual (sf_themeChosen).
export function loadThemePref() {
  try { return localStorage.getItem("sf_themeChosen") === "1" ? loadLS("sf_themeStyle", DEFAULT_THEME_STYLE) : DEFAULT_THEME_STYLE; }
  catch { return DEFAULT_THEME_STYLE; }
}

// Cores de destaque do Tático: mesmas opções do Clássico, em tons ajustados
// para o fundo grafite (bom contraste sem "estourar").
export const TATICO_ACCENTS = {
  green: "#3DD68C", blue: "#4C9AFF", red: "#E5484D", yellow: "#F5C451",
  purple: "#B48CFF", pink: "#F472B6", orange: "#FF8B4C", cyan: "#3ECFD8",
};

export const INST_BASES = [
  { id: "marinho", label: "Azul-marinho", bg: "#0A1628", bgSide: "#0C1A2F", bgCard: "#0F2138", bgInner: "#13294A", border: "#1E3556", borderIn: "#1A3050", text: "#F1F4F8", textMid: "#D6DEE9", textDim: "#A9B7CA", textFaint: "#8496AD", textGhost: "#3A5272" },
  { id: "oliva",   label: "Verde-oliva",  bg: "#0E1510", bgSide: "#111A13", bgCard: "#152018", bgInner: "#1B2A1F", border: "#2A3B2E", borderIn: "#233227", text: "#EEF2EC", textMid: "#D3DDD1", textDim: "#A7B5A5", textFaint: "#849482", textGhost: "#3D4D3F" },
  { id: "grafite", label: "Grafite",      bg: "#111214", bgSide: "#141518", bgCard: "#191B1F", bgInner: "#202328", border: "#2E3238", borderIn: "#272A30", text: "#EFF0F2", textMid: "#D5D8DC", textDim: "#A9AEB6", textFaint: "#878D96", textGhost: "#464B53" },
  { id: "vinho",   label: "Vinho",        bg: "#160A0E", bgSide: "#1B0D12", bgCard: "#221117", bgInner: "#2C161E", border: "#40222D", borderIn: "#351C25", text: "#F5EEF0", textMid: "#E2D3D7", textDim: "#BCA6AD", textFaint: "#9A838A", textGhost: "#553A43" },
];
export const INST_METALS = [
  { id: "dourado", label: "Dourado", hex: "#D4AF37" },
  { id: "prata",   label: "Prata",   hex: "#C3CAD4" },
  { id: "bronze",  label: "Bronze",  hex: "#D08A4F" },
];

// "institucional:marinho:dourado" -> { style, instBase, instMetal }
export function parseThemeStyle(str) {
  const [style, base, metal] = String(str || "").split(":");
  const ok = THEME_STYLES.some(s => s.id === style);
  return {
    style:     ok ? style : DEFAULT_THEME_STYLE,
    instBase:  INST_BASES.some(b => b.id === base)   ? base  : DEFAULT_INST_BASE,
    instMetal: INST_METALS.some(m => m.id === metal) ? metal : DEFAULT_INST_METAL,
  };
}
export function serializeThemeStyle(style, instBase, instMetal) {
  return style === "institucional" ? `institucional:${instBase}:${instMetal}` : style;
}

export function makeTheme(style, mode, accentId, instBase, instMetal) {
  // Extras que os componentes usam para se adaptar a cada estilo:
  // fontes, cantos de mira (corners), fundo animado (ambient) e cor do texto sobre o destaque (onAc).
  if (style === "classico" || !THEME_STYLES.some(s => s.id === style)) {
    const c = makeClassicTheme(mode, accentId);
    return { ...c, style: "classico", fontBody: FONT_STACKS.outfit, fontDisplay: "'Barlow Condensed','Outfit',sans-serif", fontMono: FONT_STACKS.mono,
      corners: true, ambient: true, onAc: c.mode === "dark" ? "#0a0f0b" : "#ffffff" };
  }
  if (style === "tatico") {
    const ac = TATICO_ACCENTS[accentId] || TATICO_ACCENTS.blue;
    return {
      style, mode: "dark", ac, acDim: ac + "1F", acBorder: ac + "55",
      bg: "#0E1013", bgSide: "#121418", bgCard: "#16191E", bgInner: "#1C1F25", border: "#2A2E36", borderIn: "#23272E",
      text: "#E6E8EB", textMid: "#C9CED6", textDim: "#9BA3AE", textFaint: "#7D8590", textGhost: "#4A505A",
      fontBody: FONT_STACKS.inter, fontDisplay: FONT_STACKS.barlow, fontMono: FONT_STACKS.mono,
      corners: true, ambient: false, onAc: "#0E1013",
    };
  }
  if (style === "foco") {
    if (mode === "dark") {
      const ac = "#8B87F5";
      return {
        style, mode: "dark", ac, acDim: ac + "22", acBorder: ac + "55",
        bg: "#111113", bgSide: "#141416", bgCard: "#1A1A1D", bgInner: "#222226", border: "#2E2E33", borderIn: "#26262A",
        text: "#EDEDEF", textMid: "#D4D4D8", textDim: "#A1A1AA", textFaint: "#8B8B93", textGhost: "#52525B",
        fontBody: FONT_STACKS.inter, fontDisplay: FONT_STACKS.inter, fontMono: FONT_STACKS.inter,
        corners: false, ambient: false, onAc: "#111113",
      };
    }
    const ac = "#4338CA";
    return {
      style, mode: "light", ac, acDim: ac + "14", acBorder: ac + "40",
      bg: "#F6F6F3", bgSide: "#EFEFEB", bgCard: "#FFFFFF", bgInner: "#F2F2EE", border: "#E4E4E0", borderIn: "#EEEEEA",
      text: "#18181B", textMid: "#3F3F46", textDim: "#52525B", textFaint: "#71717A", textGhost: "#A1A1AA",
      fontBody: FONT_STACKS.inter, fontDisplay: FONT_STACKS.inter, fontMono: FONT_STACKS.inter,
      corners: false, ambient: false, onAc: "#FFFFFF",
    };
  }
  // institucional
  const b = INST_BASES.find(x => x.id === instBase) || INST_BASES.find(x => x.id === DEFAULT_INST_BASE);
  const ac = (INST_METALS.find(x => x.id === instMetal) || INST_METALS.find(x => x.id === DEFAULT_INST_METAL)).hex;
  return {
    style: "institucional", mode: "dark", ac, acDim: ac + "1F", acBorder: ac + "55",
    bg: b.bg, bgSide: b.bgSide, bgCard: b.bgCard, bgInner: b.bgInner, border: b.border, borderIn: b.borderIn,
    text: b.text, textMid: b.textMid, textDim: b.textDim, textFaint: b.textFaint, textGhost: b.textGhost,
    fontBody: FONT_STACKS.source, fontDisplay: FONT_STACKS.saira, fontMono: FONT_STACKS.source,
    corners: false, ambient: false, onAc: b.bg,
  };
}


// true quando a tela é estreita (celular / tablet em pé)
export const MOBILE_QUERY = "(max-width: 820px)";
export function useIsMobile() {
  const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setM(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, []);
  return m;
}

export const ThemeCtx = createContext(null);
export const useTheme = () => useContext(ThemeCtx);
